<template>
  <div v-if="product" :class="brandClasses.ctasWrapper">
    <component
      :is="(!isOutOfStock) ? BaseSticky : 'div'"
      name="productDetailsCta"
      :stick-above-element="stickAboveElement"
    >
      <div ref="productDetailsCtaRef" class="flex gap-4 wrap gap-x-2 gap-y-6">
        <template v-if="isComingSoon || isOutOfStock">
          <product-notify-me v-if="isNotifyMe" class="w-2/3 grow" enable-scroll-to-error :size="ctaSize" />
          <vf-button
            v-else
            class="w-2/3 grow"
            disabled
            :loading="pending"
            :size="ctaSize"
          >
            {{ isComingSoon ? (product.badge || product.eyebrow)?.label : $t.outOfStock }}
          </vf-button>
        </template>
        <template v-else>
          <vf-button
            v-if="showAddToCartCta || header.expanded || $viewport.lg"
            id="pdp-add-to-cart"
            aria-live="polite"
            class="grow"
            :class="[$feature.showCheckoutOnPDP ? 'w-1/3' : 'w-2/3', { 'c-white': isSignInToBuy && theme }]"
            :disabled="!pending && (addToCartLoading || checkoutCtaLoading)"
            :loading="addToCartLoading && !checkoutCtaLoading"
            :size="ctaSize"
            :theme="isSignInToBuy ? theme : undefined"
            type="submit"
            :variant="isSignInToBuy && theme ? 'themable' : 'primary'"
            @click="addToCart"
          >
            {{ isSignInToBuy ? isNonLoyaltyMember ? $t.enrollInLoyaltyToBuy : $t.signInToBuy : $t.addToCart }}
          </vf-button>
          <vf-button
            v-if="$feature.showCheckoutOnPDP"
            class="grow"
            :class="$feature.showCheckoutOnPDP ? 'w-1/3' : 'w-2/3'"
            :disabled="!pending && (addToCartLoading || checkoutCtaLoading)"
            :loading="checkoutCtaLoading"
            :size="ctaSize"
            variant="secondary"
            @click="addToCartAndCheckout"
          >
            {{ $t.checkout }}
          </vf-button>
          <client-only>
            <checkout-apple-pay-button v-if="isApplePayEnabled" class="w-2/3 grow" @click="triggerApplePaySession" />
          </client-only>
        </template>
        <vf-button
          v-if="$feature.showFindInStoreOnPDP && !isComingSoon"
          class="w-2/3 grow"
          :size="ctaSize"
          :variant="ctaVariant"
          @click="findInStore(attributeSelectionLabels)"
        >
          <vf-icon v-if="findInStoreCta.showIcon" class="mr-2" name="location" size="md" />
          {{ $t.findInStore }}
        </vf-button>
        <vf-button
          v-if="!isDigitalProduct
            && (selectedVariant?.notifyMe && $feature.showNotifyMeOnPDP && isSelectedProductOutOfStock
              ? (!favoriteCta.hideWhenNotifyMe && $feature.catalogFavorites)
              : $feature.catalogFavorites)
          "
          :aria-label="isFavorite ? $t.removeFromFavorites : $t.favorites"
          class="grow max-w-14 p-2 <lg:max-w-12"
          :data-test-id="`pdp-${isFavorite ? 'remove' : 'add'}-favorites-cta`"
          :disabled="favorites.error"
          :loading="isFavoritePending"
          :size="ctaSize"
          :variant="isFavorite ? favoriteCta.variantSelected : ctaVariant"
          @click="toggleFavorites(productColor?.label)"
        >
          <vf-icon
            :class="{ 'c-brand-2': isFavorite }"
            :name="isFavorite ? 'heart-filled' : 'heart'"
            :size="favoriteCta.iconSize"
          />
          <span v-if="!favoriteCta.iconOnly" class="ml-2">{{ $t.favorites }}</span>
        </vf-button>
      </div>
    </component>
  </div>
</template>

<script lang="ts" setup>
import { BaseSticky } from '#components'

const {
  brandClasses,
  details: { ctaSize, ctaVariant, favoriteCta, findInStoreCta },
  showAddToCartCta,
  stickAboveElement
} = useAppConfig().pages.pdp
const { card: { signInToBuyCta: { theme } } } = useAppConfig().components.product
const favorites = useFavoritesStore()
const header = useHeaderStore()
const { $feature } = useNuxtApp()
const {
  allowApplePayOrderOnPDP,
  allowApplePayOrderOnPDPWithBopis
} = useExpressPaymentFeatures()
const {
  addToCart,
  addToCartAndCheckout,
  addToCartLoading,
  attributeSelection,
  checkoutCtaLoading,
  findInStore,
  isComingSoon,
  isDigitalProduct,
  isFavorite,
  isFavoritePending,
  isNotifyMe,
  isOutOfStock,
  isSelectedProductOutOfStock,
  isSignInToBuy,
  isNonLoyaltyMember,
  pending,
  product,
  selectedVariant,
  shippingMethod,
  toggleFavorites,
  triggerApplePaySession
} = usePdp()
const productColor = useProductColor(product)
const productDetailsCtaRef = useState<HTMLElement>('productDetailsCtaRef')

const isApplePayEnabled = computed(() => allowApplePayOrderOnPDP.value && window.ApplePaySession && (allowApplePayOrderOnPDPWithBopis.value || shippingMethod.value !== 'pickup'))

const attributeSelectionLabels = useAttributeSelectionLabels(product, attributeSelection)
</script>
